/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import * as signupAction from '@shared/store/actions/signup.action';
import * as fromSharedServices from '@shared/services';
import { Constants } from '@shared/models/constants.model';

@Injectable()
export class SignupEffects {
  sendSignup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signupAction.USER_SIGNUP),
      switchMap((action: signupAction.SignupAction) => {
        return this.signupService.doSignup(action.payload).pipe(
          tap((response) => this.signupService.saveToken(response.access_token)),
          tap((response) => this.wsService.registerAsUser(response.user.id)),
          tap((response) =>
            this.utilsService.logToServer(
              Constants.LOG_TYPES.DEVICE_INFO,
              { id: response.user.id, username: response.user.username },
              navigator.userAgent
            )
          ),
          tap((response: any) => {
            return (
              this.inviteService
                .setInviteToUser(action.payload.inviteId, response.user.id)
                // .pipe(mergeMap(() => this.emailsService.notifyInviter(action.payload)))
                .subscribe(() => {
                  /* update user invites after current invitation has toUserId updated */
                  this.inviteService
                    .updateUserInvites(action.payload.email, response.user.id, action.payload.email)
                    .subscribe();
                })
            );
          }),
          tap((response) => this.emailsService.sendVerifyEmail(response.user).subscribe()),
          map((response) => new signupAction.UserSignupSuccess(response)),
          catchError((error) => of(new signupAction.UserSignupFail(error.message)))
        );
      })
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signupAction.UPDATE_USER),
      switchMap((action: signupAction.SignupAction) => {
        return this.handleAction(action);
      })
    )
  );

  // don't emit any new actions
  saveUserForSignup$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(signupAction.SAVE_USER_FOR_SIGNUP),
        tap((action: signupAction.SaveUserForSignup) => {
          this.signupService.saveSignupUser(action.payload);
        }),
        switchMap((action: signupAction.SaveUserForSignup) => {
          return of({ type: 'NO_ACTION' });
        })
      ),
    { dispatch: false }
  );

  loadUserForSignup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signupAction.LOAD_USER_FOR_SIGNUP),
      switchMap(() => {
        const signupData = this.signupService.getSignupUser();
        return of(new signupAction.SaveUserForSignup(signupData));
      })
    )
  );

  resetUserForSignup$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(signupAction.RESET_USER),
        switchMap(() => {
          this.signupService.clearSignupUser();
          return of({ type: 'NO_ACTION' });
        })
      ),
    { dispatch: false }
  );

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signupAction.LOAD_USER),
      switchMap((action: signupAction.LoadUser) => {
        return this.handleAction(action);
      })
    )
  );

  claimAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signupAction.CLAIM_ACCOUNT),
      switchMap((action: signupAction.ClaimAccount) => {
        return this.signupService.claimAccount(action.payload).pipe(
          switchMap((response) =>
            this.userDataService.login({
              email: action.payload.email,
              password: action.payload.password,
              rememberMe: action.payload.rememberMe,
            })
          ),
          tap((response) => this.signupService.saveToken(response.access_token)),
          tap((response) => this.wsService.registerAsUser(response.user.id)),
          tap((response) =>
            this.utilsService.logToServer(
              Constants.LOG_TYPES.DEVICE_INFO,
              { id: response.user.id, username: response.user.username },
              navigator.userAgent
            )
          ),
          tap((response) => this.emailsService.sendVerifyEmail(response.user).subscribe()),
          map((response) => new signupAction.ClaimAccountSuccess(response)),
          catchError((error) => of(new signupAction.ClaimAccountFail(error.message)))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private emailsService: fromSharedServices.EmailsService,
    private inviteService: fromSharedServices.InviteService,
    private signupService: fromSharedServices.SignupService,
    private wsService: fromSharedServices.WebsocketsService,
    private utilsService: fromSharedServices.UtilsService,
    private userDataService: fromSharedServices.UserDataService
  ) {}

  handleAction(action: any) {
    return this.signupService.updateUser(action.payload).pipe(
      map((response) => new signupAction.UpdateUserSuccess(response)),
      catchError((error) => of(new signupAction.UpdateUserFail(error)))
    );
  }
}
